<script setup lang="ts">
import ClubAccountActions from '@/components/ClubAccount/ClubAccountActions.vue'
import { ClubAccountItem } from '@/components/ClubAccount/types'
import { paymentStatusLabels } from '@/Pejer/tables/paymentStatusLabels'
import { PaymentsEmbed } from '@/Pejer/tables/Payments'
import { ChevronDown, FileTextIcon } from 'lucide-vue-next'
import { Button } from 'pejer/src/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from 'pejer/src/components/ui/dropdown-menu'
import { computed } from 'vue'
import PaymentRefundModal from './PaymentRefundModal.vue'

const props = defineProps<{
  payment: PaymentsEmbed.ListItem
}>()

const invoiceUrl = computed(() => props.payment.kidNorwayInvoice?.invoiceUrl)

const canRefund = computed(() =>
  ['InitiatedPayment', 'InvoiceConfirmed', 'ChargedPayment'].includes(props.payment.paymentStatus)
)

const isClubAccount = computed(
  () =>
    props.payment.fundsLocation === 'ClubAccount' ||
    !!props.payment.kidNorwayInvoice?.receiverIsDirectPaymentAccount
)

// The payments schema converts grossAmount to kroner, but kidNorwayInvoice.paidAmount stays in øre.
const clubAccountItem = computed<ClubAccountItem>(() => {
  const paidOere = props.payment.kidNorwayInvoice?.paidAmount
  return {
    paymentReferenceID: props.payment.paymentReferenceID,
    description: props.payment.description,
    paymentStatus: props.payment.paymentStatus,
    customerName: props.payment.customer?.name,
    kid: props.payment.kidNorwayInvoice?.kid,
    grossKroner: props.payment.grossAmount,
    paidKroner:
      paidOere != null
        ? paidOere / 100
        : props.payment.paymentStatus === 'ChargedPayment'
          ? props.payment.grossAmount
          : 0,
  }
})
</script>
<template>
  <DropdownMenu>
    <DropdownMenuTrigger as-child>
      <Button variant="ghost" class="h-8 hover:bg-slate-200">
        <span>Valg</span>
        <ChevronDown class="w-4 h-4 shrink-0 ml-1" />
      </Button>
    </DropdownMenuTrigger>
    <DropdownMenuContent align="end">
      <DropdownMenuLabel>Valg</DropdownMenuLabel>
      <template v-if="invoiceUrl">
        <DropdownMenuSeparator />
        <DropdownMenuGroup class="font-semibold">
          <DropdownMenuItem class="p-0 text-slate-700">
            <a class="w-full" :href="invoiceUrl" target="_blank">
              <Button class="w-full gap-2 flex flex-nowrap text-left justify-start" variant="ghost">
                <FileTextIcon class="w-4 h-4" />Se Faktura
              </Button>
            </a>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>
      <ClubAccountActions v-if="isClubAccount" :item="clubAccountItem" />
      <template v-else>
        <DropdownMenuSeparator />
        <DropdownMenuGroup class="text-red-500 font-semibold">
          <DropdownMenuItem class="p-0">
            <PaymentRefundModal :payment="payment">
              <Button
                @click.prevent.stop
                class="p-2 rounded-sm w-full m-0 cursor-pointer flex-1 justify-start hover:bg-slate-100 flex flex-col h-fit"
                variant="ghost"
                :disabled="!canRefund"
              >
                Refunder
                <span v-if="!canRefund" class="text-xs text-gray-800 font-normal">
                  Kan ikke refunderes når status er «{{ paymentStatusLabels[payment.paymentStatus] }}»
                </span>
              </Button>
            </PaymentRefundModal>
          </DropdownMenuItem>
        </DropdownMenuGroup>
      </template>
    </DropdownMenuContent>
  </DropdownMenu>
</template>
